import React from "react";
import { prepFqReportStyles } from "../styles/prepFqReportStyles";
import { useTranslation } from "next-i18next";
import { useAuth } from "../../../firebase/useAuth";
import useUser from "../../../firebase/useUser";
import usePrepFqProfile from "../fetchers/usePrepFqProfile";
import { useLearningModules } from "../fetchers/learning_modules/useLearningModules";
import { Paper } from "@mui/material";
import clsx from "clsx";
import HourglassIcon from "../icons/HourglassIcon";
import { useState } from "react";
import { LearningModule } from "../../../models/learningModules";
import { useEffect } from "react";
import { journalEntriesStyles } from "../styles/journalEntriesStyles";
import Highlighter from "react-highlight-words";
import { useResizeDetector } from "react-resize-detector";
import Skeleton from "@mui/material/Skeleton";

interface Props {
  topicModalOpen: boolean;
  setTopicModalOpen(boolean): any;
  setSelectedModuleId(string): any;
  setHeight(number): any;
}

const NotYetApplicableCard: React.FC<Props> = ({
  topicModalOpen,
  setTopicModalOpen,
  setSelectedModuleId,
  setHeight,
}) => {
  const { t } = useTranslation(["prepFq"]);
  const classes = prepFqReportStyles();
  const journalEntriesClasses = journalEntriesStyles();
  const [notApplicableModules, setNotApplicableModules] = useState<
    LearningModule[]
  >([]);
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { profile, isLoading } = usePrepFqProfile(user?.user_id);
  const { learningModules } = useLearningModules();
  const { height: rightColHeight, ref } = useResizeDetector();
  useEffect(() => {
    if (rightColHeight > 350) {
      setHeight(rightColHeight + 58);
    }
  }, [rightColHeight, setHeight]);

  useEffect(() => {
    const missingModules = [...learningModules].filter(
      (module) =>
        module.ignore_in_app === false &&
        !module.module_id.includes("behaviour_change_guide")
    );
    profile?.suggestions?.doing_well.forEach((prepFqResult) => {
      const moduleIndex = missingModules.findIndex(
        (module) => prepFqResult.module_id === module.module_id
      );
      if (moduleIndex !== -1) {
        missingModules.splice(moduleIndex, 1);
      }
    });
    profile?.suggestions?.little_improvement.forEach((prepFqResult) => {
      const moduleIndex = missingModules.findIndex(
        (module) => prepFqResult.module_id === module.module_id
      );
      if (moduleIndex !== -1) {
        missingModules.splice(moduleIndex, 1);
      }
    });
    profile?.suggestions?.lots_of_improvements.forEach((prepFqResult) => {
      const moduleIndex = missingModules.findIndex(
        (module) => prepFqResult.module_id === module.module_id
      );
      if (moduleIndex !== -1) {
        missingModules.splice(moduleIndex, 1);
      }
    });
    setNotApplicableModules(missingModules);
  }, [
    learningModules,
    profile?.suggestions?.doing_well,
    profile?.suggestions?.little_improvement,
    profile?.suggestions?.lots_of_improvements,
  ]);

  const loadingContainer = (index) => {
    return (
      <Skeleton
        key={`loading-${index}`}
        variant="rectangular"
        style={{ width: "100%", marginBottom: 16, maxWidth: 600 }}
        height={24}
      />
    );
  };

  return (
    <React.Fragment>
      <Paper
        elevation={0}
        className={clsx(
          classes.borderedCard,
          classes.borderedCardAlt,
          "twenty-third-step-not-applicable"
        )}
        ref={ref}
      >
        <div className={classes.suggestionIconContainer}>
          <HourglassIcon
            className={clsx(classes.suggestionIconSize, classes.lotsIcon)}
          />
        </div>
        <h3 className={classes.suggestionsTitle}>
          {t("prepFq:report.notApplicable.title")}
        </h3>
        <ul className={journalEntriesClasses.notApplicableList}>
          {isLoading &&
            Array(3)
              .fill(0)
              .map((_, index) => {
                return loadingContainer(index);
              })}
          {!isLoading &&
            notApplicableModules.map((module, index) => {
              return (
                <li
                  onClick={() => {
                    if (topicModalOpen && index === 0) {
                      setSelectedModuleId(module.module_id);
                      setTopicModalOpen(true);
                    } else if (!topicModalOpen) {
                      setSelectedModuleId(module.module_id);
                      setTopicModalOpen(true);
                    }
                  }}
                  key={`${module.question_id}-${index}`}
                  className={clsx(classes.suggestionsListItem)}
                  style={{
                    cursor:
                      index === 0 && topicModalOpen ? "pointer" : undefined,
                    marginBottom: 4,
                  }}
                >
                  <div
                    className={clsx(classes.suggestionsListItemText, {
                      [classes.suggestionsListItemTextAfter]:
                        !topicModalOpen || (topicModalOpen && index === 0),
                      [classes.suggestionsListItemTextAfterAlt]:
                        !topicModalOpen || (topicModalOpen && index === 0),
                    })}
                  >
                    <Highlighter
                      searchWords={
                        module.module_name
                          ? [
                              module.module_name.slice(
                                module.module_name.lastIndexOf(" ")
                              ),
                            ]
                          : [""]
                      }
                      autoEscape={true}
                      textToHighlight={
                        module.module_name ? module.module_name : ""
                      }
                    />
                  </div>
                </li>
              );
            })}
        </ul>
        <div className={clsx(classes.goButtonContainerAlt)}></div>
      </Paper>
    </React.Fragment>
  );
};

export default NotYetApplicableCard;
